import React, { useCallback, useState } from 'react'

import Link from 'next/link'
import {
  HiMiniChevronRight,
  HiOutlineArrowTopRightOnSquare,
} from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { MiniBadge } from '@/components/common/Badge'
import CommonLink from '@/components/common/Link'
import { isActive } from '@/components/common/Sidebar/lib'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import { DEMO_URL } from '@/constants/general'

import { useMediaQuery } from '@/hooks/common/useMediaQuery'
import { usePathname } from '@/hooks/common/usePathname'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

import type { NavigationOption } from '@/types/navigation/Sidebar'

interface SidebarOptionChildrenContentProps {
  name: string
  optionChildren: NavigationOption[]
}

export const SidebarOptionChildrenContent: React.FC<
  SidebarOptionChildrenContentProps
> = ({ name, optionChildren }) => {
  const { activeOrgId } = useActiveOrgId()
  return (
    <div className="flex flex-col gap-y-1 py-1 px-1">
      <div className="px-2">
        <span className="text-xs font-medium text-primary-500 dark:text-primary-400">
          {name}
        </span>
      </div>
      <div className="flex flex-col">
        {optionChildren.map((child, idx) => (
          <Link
            key={idx}
            className={twMerge(
              'text-sm text-start font-normal rounded-[4px] px-2 py-1 text-primary-700 dark:text-primary-300',
              'hover:bg-primary-200 dark:hover:bg-primary-800 hover:text-primary-900 dark:hover:text-primary-100',
            )}
            href={
              child.isOrganizationFeature
                ? `/orgs/${activeOrgId}${child.href}`
                : child.href
            }
          >
            {child.name}
          </Link>
        ))}
      </div>
    </div>
  )
}

interface SidebarOptionProps {
  option: NavigationOption
  activeOverride?: boolean
  count?: number
  canAccess?: boolean
  expanded?: boolean
}

export const SidebarOption: React.FC<SidebarOptionProps> = ({
  option,
  activeOverride,
  count,
  canAccess = true,
  expanded = false,
}) => {
  const { activeOrgId } = useActiveOrgId()

  const pathname = usePathname()
  const isMdOrGreater = useMediaQuery('(min-width: 768px)')

  const [open, setOpen] = useState<boolean>(false)

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // if the option has children, open the tooltip on any click
      if (!isMdOrGreater && option.children && option.children.length > 0) {
        e.preventDefault()
        e.stopPropagation()
        setOpen(true)
      }
      if (option.href !== '#') {
        return
      }
      if (option.children) {
        e.preventDefault()
        setOpen(true)
      }
    },
    [
      option.href,
      option.children,
      isMdOrGreater,
      activeOrgId,
      pathname,
      activeOverride,
      setOpen,
    ],
  )

  const handleMobileMouseLeave = useCallback(() => {
    // this is important for mobile view, where we want to make sure the user
    // can click anywhere outside of the tooltip to close it
    if (!isMdOrGreater) {
      setOpen(false)
    }
  }, [isMdOrGreater, setOpen])

  return (
    <Tooltip delayDuration={0} open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        asChild={true}
        onClick={handleClick}
        onMouseLeave={handleMobileMouseLeave}
      >
        <Link
          href={
            // don't allow clicking on the link if the activeOrgId has not loaded yet
            activeOrgId === ''
              ? '#'
              : option.isOrganizationFeature
                ? `/orgs/${activeOrgId}${option.href}`
                : option.href
          }
          className={twMerge(
            'group relative flex items-center justify-center h-8 rounded-md text-sm text-primary-900 dark:text-primary-100',
            canAccess && activeOrgId !== ''
              ? isActive(option, pathname, activeOverride)
                ? 'bg-blue-100/80 hover:bg-blue-200/80 dark:bg-accent-600/20 hover:dark:bg-accent-600/40 font-medium text-blue-600 dark:text-accent-400'
                : 'hover:bg-primary-200 dark:hover:bg-primary-800 hover:text-primary-900 dark:hover:text-primary-100'
              : 'opacity-50',
            expanded ? 'px-[6px] w-full' : 'w-8',
          )}
        >
          <div
            className={twMerge(
              'flex items-center w-full gap-x-2',
              expanded ? 'justify-start' : 'justify-center',
            )}
          >
            <div className="relative flex items-center">
              {option.icon !== null &&
                (option.activeIcon !== null &&
                isActive(option, pathname, activeOverride) ? (
                  <option.activeIcon size={20} />
                ) : (
                  <option.icon size={20} />
                ))}
              {count !== undefined && count > 0 && (
                <div
                  className={twMerge(
                    'absolute -top-[7px] -right-[7px] rounded-full ring-2 ring-primary-50 dark:ring-default-dark',
                  )}
                >
                  <MiniBadge count={count} variant="danger" />
                </div>
              )}
            </div>

            {expanded && (
              <div className="flex items-center justify-between w-full">
                <span className="text-[13px] font-medium whitespace-nowrap">
                  {option.name}
                </span>
                {option.children && option.children.length > 0 && (
                  <HiMiniChevronRight className="flex-shrink-0 w-4 h-4" />
                )}
              </div>
            )}
          </div>
        </Link>
      </TooltipTrigger>
      {!canAccess ? (
        <TooltipContent
          align="start"
          side={isMdOrGreater ? 'right' : 'bottom'}
          sideOffset={7}
          inverseColors={expanded ? true : false}
          className="max-w-[320px]"
        >
          {option.disabledTooltip ?? (
            <div className="flex flex-col py-[2px]">
              {!expanded && <span>{option.name}</span>}
              <CommonLink
                href={DEMO_URL}
                className={twMerge(
                  'flex items-center gap-x-1',
                  expanded ? '' : 'text-blue-300 dark:text-blue-600',
                )}
              >
                <HiOutlineArrowTopRightOnSquare className="flex-shrink-0 w-4 h-4" />
                Book a demo
              </CommonLink>
            </div>
          )}
        </TooltipContent>
      ) : option.children ? (
        <TooltipContent
          align="start"
          side={isMdOrGreater ? 'right' : 'bottom'}
          sideOffset={5}
          inverseColors={true}
          className="p-0"
        >
          <SidebarOptionChildrenContent
            name={option.name}
            optionChildren={option.children}
          />
        </TooltipContent>
      ) : (
        <TooltipContent
          side="right"
          sideOffset={7}
          className={twMerge(
            // need this logic in order to hide the tooltip animation when collapsing;
            // if we instead just return null in the ternary expression, the animation
            // will temporarily flash as the sidebar collapses
            expanded ? 'hidden pointer-events-none' : '',
          )}
        >
          <span>{option.name}</span>
        </TooltipContent>
      )}
    </Tooltip>
  )
}

export default SidebarOption
